import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { getAuthToken } from "../../../utils/getAuthToken";

// Category drill-down read (Phase 5, 2026-08-15) -- backs
// CategoryReportDetail.jsx against the real /reports/category endpoint
// (Phase 4). Same debounce-via-condition + requestId race-guard pattern
// already fixed on ReportsSlice.js's fetchReports (see [[reports-read-endpoint-frontend-plan]]
// memory) -- built in from day one here instead of retrofitted.
const SERVER_DOMAIN = process.env.REACT_APP_SERVER_DOMAIN || "http://localhost:8000";

const debounceMap = new Map();
const DEBOUNCE_TIME = 1500; // shorter than Overview's 5s -- category switches/cursor clicks are more frequent, single-purpose actions, not a filter form someone might resubmit accidentally.

const shouldDebounce = (key) => {
  const now = Date.now();
  const lastCall = debounceMap.get(key);
  if (lastCall && now - lastCall < DEBOUNCE_TIME) return true;
  debounceMap.set(key, now);
  return false;
};

export const __resetCategoryReportsCaches = () => {
  debounceMap.clear();
};

// groupByCount/countMin/countMax/countSortDir -- "Filter by Count" grouped
// mode (2026-08-15). Cursor shape differs between the two modes
// ({timestamp,id} for flat events, {count,contactId} for grouped
// conversations) -- the debounce key just needs SOME stable string per
// cursor value, so it stringifies whichever shape shows up rather than
// hardcoding field names.
const buildDebounceKey = ({ category, startTime, endTime, memberIds, durationMin, durationMax, groupByCount, countMin, countMax, countSortDir, cursor, pageSize }) =>
  `fetchCategoryReportEvents_${category}_${startTime}_${endTime}_${memberIds || ""}_${durationMin ?? ""}_${durationMax ?? ""}_${groupByCount ? "grouped" : ""}_${countMin ?? ""}_${countMax ?? ""}_${countSortDir ?? ""}_${cursor ? JSON.stringify(cursor) : ""}_${pageSize ?? ""}`;

// Raw network call, factored out of the thunk below so the "export all
// pages" loop (CategoryReportDetail.jsx) can reuse the exact same request
// logic without going through Redux dispatch/state (an export shouldn't
// clobber whatever page is currently on screen, and doesn't want the
// thunk's debounce -- it's one legitimate burst of sequential calls, not a
// user re-submitting a filter).
export const fetchCategoryReportEventsRequest = async ({ category, startTime, endTime, memberIds, durationMin, durationMax, groupByCount, countMin, countMax, countSortDir, cursor, pageSize }) => {
  const token = await getAuthToken();
  if (!token) throw new Error("No token found");

  const response = await fetch(`${SERVER_DOMAIN}/api/teammembers/reports/category`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
    body: JSON.stringify({ category, startTime, endTime, memberIds, durationMin, durationMax, groupByCount, countMin, countMax, countSortDir, cursor, pageSize }),
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    // message first, not error -- the "Filter by Count" safety refusal
    // (422 TOO_MANY_EVENTS_TO_GROUP) sends a short machine code in
    // `error` and the actual user-facing text in `message`; prefer
    // showing people the sentence, not the code.
    throw new Error(errorData.message || errorData.error || "Failed to fetch category report events");
  }
  return response.json();
};

export const fetchCategoryReportEvents = createAsyncThunk(
  "categoryReports/fetchCategoryReportEvents",
  async (arg, { rejectWithValue }) => {
    try {
      return await fetchCategoryReportEventsRequest(arg);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
  {
    condition: (arg) => !shouldDebounce(buildDebounceKey(arg)),
  }
);

// Same row identity the table uses for React keys: flat events have a real id, grouped
// rows are one per (conversation, performer) pair.
const rowIdentity = (row) => row.id ?? `${row.conversationId}_${row.performerName ?? ""}`;

const categoryReportsSlice = createSlice({
  name: "categoryReports",
  initialState: {
    rows: [],
    nextCursor: null,
    loading: false,
    // Infinite scroll: a request that carries a cursor appends to `rows` instead of
    // replacing them, so it must NOT flip `loading` (that swaps the whole table for
    // skeleton rows).
    loadingMore: false,
    loadMoreError: null,
    error: null,
    currentRequestId: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchCategoryReportEvents.pending, (state, action) => {
        state.currentRequestId = action.meta.requestId;
        if (action.meta.arg.cursor) {
          state.loadingMore = true;
          state.loadMoreError = null;
          return;
        }
        state.loading = true;
        state.loadingMore = false;
        state.loadMoreError = null;
        state.error = null;
      })
      .addCase(fetchCategoryReportEvents.fulfilled, (state, action) => {
        if (action.meta.requestId !== state.currentRequestId) return; // stale, superseded response
        if (action.meta.arg.cursor) {
          const seen = new Set(state.rows.map(rowIdentity));
          state.rows.push(...action.payload.rows.filter((row) => !seen.has(rowIdentity(row))));
          state.loadingMore = false;
          state.nextCursor = action.payload.nextCursor;
          return;
        }
        state.loading = false;
        state.loadingMore = false;
        state.rows = action.payload.rows;
        state.nextCursor = action.payload.nextCursor;
        state.error = null;
      })
      .addCase(fetchCategoryReportEvents.rejected, (state, action) => {
        if (action.meta.requestId !== state.currentRequestId) return;
        if (action.meta.arg.cursor) {
          // Keep what's already on screen; the table shows a retry row instead.
          state.loadingMore = false;
          state.loadMoreError = action.payload || "Couldn't load more rows.";
          return;
        }
        state.loading = false;
        state.loadingMore = false;
        state.rows = [];
        state.nextCursor = null;
        state.error = action.payload;
      });
  },
});

export default categoryReportsSlice.reducer;
